import React, { FormEvent, useMemo, useState } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import {
  createDocumentPostingRule,
  deleteDocumentPostingRule,
  updateDocumentPostingRule,
} from '../../services/mutations';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { normalizePaginatedListResponse } from '../../services/paginatedList';
import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DocumentTextIcon,
  GeneralList,
  Label,
  ListTreeIcon,
  PencilIcon,
  PlusIcon,
  Select,
  Switch,
  TextInput,
  TrashIcon,
  useConfirm,
} from '../../components/ui';
import { CoaAccountPickerModal } from '../accounting/shared/CoaAccountPickerModal';
import {
  DocumentPostingAmountMode,
  DocumentPostingRole,
  DocumentPostingRule,
  DocumentPostingSide,
  useDocumentFileTypes,
  useDocumentPostingRules,
} from './useData';

type AccCompany = { id: number; code: string; name: string; isActive?: boolean };
type AccAccount = { id: number; code: string; name: string; combLevel?: string | null };
type AccDocumentType = { id: number; code: string; name: string; isActive?: boolean };

type LineDraft = {
  sortOrder: number;
  role: DocumentPostingRole;
  side: DocumentPostingSide;
  accountId: number | '';
  amountMode: DocumentPostingAmountMode;
  matchKeywords: string;
};

type RuleDraft = {
  id?: number;
  code: string;
  name: string;
  fileTypeId: number | '';
  supplierKey: string;
  documentTypeId: number | '' | null;
  defaultCompanyId: number | '' | null;
  isActive: boolean;
  lines: LineDraft[];
};

const ROLES: DocumentPostingRole[] = ['EXPENSE', 'VAT', 'PAYABLE', 'RECEIVABLE', 'OTHER'];
const SIDES: DocumentPostingSide[] = ['DEBIT', 'CREDIT'];
const AMOUNT_MODES: { value: DocumentPostingAmountMode; label: string; short: string }[] = [
  { value: 'SCAN_LINES', label: 'Scan line amounts', short: 'Scan lines' },
  { value: 'TOTAL', label: 'Net / document total', short: 'Total' },
  { value: 'VAT_AMOUNT', label: 'VAT amount', short: 'VAT' },
  { value: 'BALANCE', label: 'Balance (remainder)', short: 'Balance' },
];

const blankExpense = (sortOrder: number, keywords = ''): LineDraft => ({
  sortOrder,
  role: 'EXPENSE',
  side: 'DEBIT',
  accountId: '',
  amountMode: 'TOTAL',
  matchKeywords: keywords,
});

const PRESETS: Record<string, LineDraft[]> = {
  INVOICE: [
    { sortOrder: 0, role: 'EXPENSE', side: 'DEBIT', accountId: '', amountMode: 'TOTAL', matchKeywords: '' },
    { sortOrder: 1, role: 'VAT', side: 'DEBIT', accountId: '', amountMode: 'VAT_AMOUNT', matchKeywords: '' },
    { sortOrder: 2, role: 'PAYABLE', side: 'CREDIT', accountId: '', amountMode: 'BALANCE', matchKeywords: '' },
  ],
  CREDIT_NOTE: [
    { sortOrder: 0, role: 'EXPENSE', side: 'CREDIT', accountId: '', amountMode: 'TOTAL', matchKeywords: '' },
    { sortOrder: 1, role: 'PAYABLE', side: 'DEBIT', accountId: '', amountMode: 'BALANCE', matchKeywords: '' },
  ],
  BUNKER: [
    { sortOrder: 0, role: 'EXPENSE', side: 'DEBIT', accountId: '', amountMode: 'TOTAL', matchKeywords: '' },
    { sortOrder: 1, role: 'PAYABLE', side: 'CREDIT', accountId: '', amountMode: 'BALANCE', matchKeywords: '' },
  ],
  PDA: [
    { sortOrder: 0, role: 'EXPENSE', side: 'DEBIT', accountId: '', amountMode: 'TOTAL', matchKeywords: '' },
    { sortOrder: 1, role: 'PAYABLE', side: 'CREDIT', accountId: '', amountMode: 'BALANCE', matchKeywords: '' },
  ],
};

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  return normalizePaginatedListResponse<T>(data as any, 1, 500).data;
}

function accountLabel(a: AccAccount) {
  return a.name || a.combLevel || a.code;
}

function amountModeShort(mode: DocumentPostingAmountMode) {
  return AMOUNT_MODES.find((m) => m.value === mode)?.short || mode;
}

function amountModeLabel(mode: DocumentPostingAmountMode) {
  return AMOUNT_MODES.find((m) => m.value === mode)?.label || mode;
}

const emptyDraft = (): RuleDraft => ({
  code: '',
  name: '',
  fileTypeId: '',
  supplierKey: '',
  documentTypeId: '',
  defaultCompanyId: '',
  isActive: true,
  lines: PRESETS.INVOICE.map((l) => ({ ...l })),
});

const SideMark: React.FC<{ side: DocumentPostingSide; compact?: boolean; isDark?: boolean }> = ({
  side,
  compact,
  isDark = false,
}) => (
  <span
    className={cn(
      'inline-flex items-center justify-center font-semibold tabular-nums',
      compact ? 'h-5 min-w-[1.5rem] rounded px-1 text-[10px]' : 'h-6 min-w-[2rem] rounded-md px-1.5 text-[11px]',
      side === 'DEBIT'
        ? isDark
          ? 'bg-teal-400/15 text-teal-200'
          : 'bg-teal-600/10 text-teal-800'
        : isDark
          ? 'bg-amber-400/15 text-amber-200'
          : 'bg-amber-600/10 text-amber-900',
    )}
  >
    {side === 'DEBIT' ? 'Dr' : 'Cr'}
  </span>
);

const PostingChain: React.FC<{
  lines: Array<{ role: string; side: DocumentPostingSide; account?: string | null }>;
  colors: ReturnType<typeof useTheme>['colors'];
  isDark?: boolean;
}> = ({ lines, colors, isDark = false }) => {
  if (!lines.length) {
    return <span className={cn('text-sm', colors.textMuted)}>No roles</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {lines.map((line, index) => (
        <React.Fragment key={`${line.role}-${index}`}>
          {index > 0 && <ChevronRightIcon className={cn('h-3 w-3 shrink-0 opacity-40', colors.textMuted)} />}
          <span className="inline-flex items-center gap-1.5 text-sm">
            <SideMark side={line.side} compact isDark={isDark} />
            <span className={cn('font-medium', colors.text)}>{line.role}</span>
            {line.account && (
              <span className={cn('hidden max-w-[10rem] truncate text-xs xl:inline', colors.textMuted)} title={line.account}>
                {line.account}
              </span>
            )}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

export const BusinessRules: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const confirm = useConfirm();
  const { data: rules = [], mutate, isLoading } = useDocumentPostingRules();
  const { data: fileTypes = [] } = useDocumentFileTypes();
  const { data: companiesRaw } = useSWR('/acc-companies?page=1&limit=500', fetcher);
  // Omit page/limit so the API returns the full COA (pagination is capped at 200).
  const { data: accountsRaw } = useSWR('/acc-accounts', fetcher);
  const { data: docTypesRaw } = useSWR('/acc-document-types?page=1&limit=200', fetcher);

  const companies = useMemo(() => asList<AccCompany>(companiesRaw).filter((c) => c.isActive !== false), [companiesRaw]);
  const accounts = useMemo(() => asList<AccAccount>(accountsRaw), [accountsRaw]);
  const docTypes = useMemo(() => asList<AccDocumentType>(docTypesRaw).filter((d) => d.isActive !== false), [docTypesRaw]);

  const [editing, setEditing] = useState<RuleDraft | null>(null);
  const [filterFileTypeId, setFilterFileTypeId] = useState<number | 'all'>('all');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [coaPickerLineIndex, setCoaPickerLineIndex] = useState<number | null>(null);

  const filteredRules = useMemo(() => {
    if (filterFileTypeId === 'all') return rules;
    return rules.filter((r) => r.fileTypeId === filterFileTypeId);
  }, [rules, filterFileTypeId]);

  const fileTypeTabs = useMemo(
    () => [
      { id: 'all', label: 'All', count: rules.length },
      ...fileTypes.map((ft) => ({
        id: String(ft.id),
        label: ft.name,
        count: rules.filter((r) => r.fileTypeId === ft.id).length,
      })),
    ],
    [fileTypes, rules],
  );

  const activeTab = filterFileTypeId === 'all' ? 'all' : String(filterFileTypeId);

  const openEditor = (rule?: DocumentPostingRule) => {
    setError(null);
    if (!rule) {
      const draft = emptyDraft();
      if (filterFileTypeId !== 'all') {
        const ft = fileTypes.find((f) => f.id === filterFileTypeId);
        const code = (ft?.code || 'INVOICE').toUpperCase();
        draft.fileTypeId = filterFileTypeId;
        draft.lines = (PRESETS[code] || PRESETS.INVOICE).map((l) => ({ ...l }));
        draft.code = `${code}_DEFAULT`;
        draft.name = `${ft?.name || code} default`;
      }
      setEditing(draft);
      return;
    }
    setEditing({
      id: rule.id,
      code: rule.code,
      name: rule.name,
      fileTypeId: rule.fileTypeId,
      supplierKey: rule.supplierKey || '',
      documentTypeId: rule.documentTypeId ?? '',
      defaultCompanyId: rule.defaultCompanyId ?? '',
      isActive: rule.isActive !== false,
      lines: rule.lines.length
        ? rule.lines.map((l, i) => ({
            sortOrder: l.sortOrder ?? i,
            role: l.role,
            side: l.side,
            accountId: l.accountId,
            amountMode: l.amountMode,
            matchKeywords: l.matchKeywords || '',
          }))
        : PRESETS.INVOICE.map((l) => ({ ...l })),
    });
  };

  const duplicateRule = (rule: DocumentPostingRule) => {
    setError(null);
    const existingCodes = new Set(rules.map((r) => r.code.toUpperCase()));
    const base = rule.code.replace(/_COPY\d*$/i, '').slice(0, 32);
    let code = `${base}_COPY`.slice(0, 40);
    let n = 2;
    while (existingCodes.has(code.toUpperCase())) {
      code = `${base}_COPY${n}`.slice(0, 40);
      n += 1;
    }
    setEditing({
      code,
      name: `${rule.name} (copy)`.slice(0, 120),
      fileTypeId: rule.fileTypeId,
      supplierKey: rule.supplierKey || '',
      documentTypeId: rule.documentTypeId ?? '',
      defaultCompanyId: rule.defaultCompanyId ?? '',
      isActive: rule.isActive !== false,
      lines: rule.lines.length
        ? rule.lines.map((l, i) => ({
            sortOrder: l.sortOrder ?? i,
            role: l.role,
            side: l.side,
            accountId: l.accountId,
            amountMode: l.amountMode,
            matchKeywords: l.matchKeywords || '',
          }))
        : PRESETS.INVOICE.map((l) => ({ ...l })),
    });
  };

  const applyPreset = () => {
    if (!editing) return;
    const ft = fileTypes.find((f) => f.id === Number(editing.fileTypeId));
    const code = (ft?.code || 'INVOICE').toUpperCase();
    const preset = PRESETS[code] || PRESETS.INVOICE;
    setEditing({
      ...editing,
      lines: preset.map((l) => ({ ...l })),
      code: editing.code || `${code}_DEFAULT`,
      name: editing.name || `${ft?.name || code} default`,
    });
  };

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    if (!editing) return;
    setEditing({
      ...editing,
      lines: editing.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)),
    });
  };

  const rulePayload = (rule: {
    code: string;
    name: string;
    fileTypeId: number;
    supplierKey: string;
    documentTypeId: number | null;
    defaultCompanyId: number | null;
    isActive: boolean;
    lines: Array<{
      role: DocumentPostingRole;
      side: DocumentPostingSide;
      accountId: number;
      amountMode: DocumentPostingAmountMode;
      matchKeywords?: string | null;
    }>;
  }) => ({
    code: rule.code.trim(),
    name: rule.name.trim(),
    fileTypeId: rule.fileTypeId,
    supplierKey: rule.supplierKey.trim() || '',
    documentTypeId: rule.documentTypeId,
    defaultCompanyId: rule.defaultCompanyId,
    isActive: rule.isActive,
    lines: rule.lines.map((line, index) => ({
      sortOrder: index,
      role: line.role,
      side: line.side,
      accountId: line.accountId,
      amountMode: line.amountMode,
      matchKeywords: line.matchKeywords?.trim() || null,
    })),
  });

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      if (!editing.fileTypeId) throw new Error('Select a file type.');
      if (!editing.lines.length || editing.lines.some((l) => !l.accountId)) {
        throw new Error('Each role line needs an account.');
      }
      const body = rulePayload({
        code: editing.code,
        name: editing.name,
        fileTypeId: Number(editing.fileTypeId),
        supplierKey: editing.supplierKey,
        documentTypeId: editing.documentTypeId ? Number(editing.documentTypeId) : null,
        defaultCompanyId: editing.defaultCompanyId ? Number(editing.defaultCompanyId) : null,
        isActive: editing.isActive,
        lines: editing.lines.map((line) => ({
          role: line.role,
          side: line.side,
          accountId: Number(line.accountId),
          amountMode: line.amountMode,
          matchKeywords: line.matchKeywords,
        })),
      });
      if (editing.id) await updateDocumentPostingRule(editing.id, body);
      else await createDocumentPostingRule(body);
      setEditing(null);
      await mutate();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to save rule';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule: DocumentPostingRule) => {
    if (!rule.id || !rule.lines?.length || rule.lines.some((l) => !l.accountId)) return;
    setError(null);
    try {
      await updateDocumentPostingRule(
        rule.id,
        rulePayload({
          code: rule.code,
          name: rule.name,
          fileTypeId: rule.fileTypeId,
          supplierKey: rule.supplierKey || '',
          documentTypeId: rule.documentTypeId ?? null,
          defaultCompanyId: rule.defaultCompanyId ?? null,
          isActive: rule.isActive === false,
          lines: rule.lines.map((line) => ({
            role: line.role,
            side: line.side,
            accountId: line.accountId,
            amountMode: line.amountMode,
            matchKeywords: line.matchKeywords,
          })),
        }),
      );
      await mutate();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to update Active';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  };

  if (editing) {
    const previewLines = editing.lines.map((line) => {
      const account = accounts.find((a) => a.id === line.accountId);
      return {
        role: line.role,
        side: line.side,
        accountLabel: account ? accountLabel(account) : null,
        amountMode: amountModeShort(line.amountMode),
      };
    });

    return (
      <div className="p-4 md:p-6">
        <form onSubmit={save} className={cn('mx-auto max-w-5xl overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
          <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4', colors.border)}>
            <div className="flex min-w-0 items-start gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditing(null);
                  setCoaPickerLineIndex(null);
                  setError(null);
                }}
                aria-label="Back"
                title="Back"
                className="mt-0.5 shrink-0"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
              <h2 className={cn('text-lg font-semibold tracking-tight', colors.text)}>
                {editing.id ? 'Edit rule' : 'New rule'}
              </h2>
              <p className={cn('mt-0.5 text-sm', colors.textMuted)}>
                Define how a scanned document posts to debit and credit accounts.
              </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                label="Active"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(null);
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

          {error && (
            <div className={cn('border-b px-5 py-3 text-sm', colors.border, colors.error)}>
              {error}
            </div>
          )}

          <div className={cn('border-b px-5 py-4', colors.border, colors.headerBg)}>
            <p className={cn('mb-3 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Posting preview</p>
            <div className="flex flex-wrap items-stretch gap-2">
              {previewLines.length === 0 ? (
                <p className={cn('text-sm', colors.textMuted)}>Add roles below to preview the journal.</p>
              ) : (
                previewLines.map((line, index) => (
                  <React.Fragment key={`${line.role}-${index}`}>
                    {index > 0 && (
                      <div className="flex items-center px-0.5">
                        <ChevronRightIcon className={cn('h-4 w-4 opacity-35', colors.textMuted)} />
                      </div>
                    )}
                    <div
                      className={cn(
                        'min-w-[9.5rem] flex-1 rounded-xl border px-3 py-2.5',
                        colors.border,
                        colors.surface,
                        line.side === 'DEBIT' ? 'border-t-[3px] border-t-teal-600' : 'border-t-[3px] border-t-amber-500',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn('text-sm font-semibold', colors.text)}>{line.role}</span>
                        <SideMark side={line.side} isDark={isDark} />
                      </div>
                      <p className={cn('mt-1 text-[11px]', colors.textMuted)}>{line.amountMode}</p>
                      <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)} title={line.accountLabel || undefined}>
                        {line.accountLabel || 'Account not set'}
                      </p>
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          <div className="space-y-8 px-5 py-6">
            <section>
              <h3 className={cn('mb-3 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Identity</h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Label required>Name</Label>
                  <TextInput
                    required
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Invoice default"
                    showClearButton={false}
                  />
                </div>
                <div>
                  <Label required>Code</Label>
                  <TextInput
                    required
                    value={editing.code}
                    onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                    placeholder="INVOICE_DEFAULT"
                    showClearButton={false}
                  />
                </div>
                <div className="sm:col-span-3">
                  <Label required>File type</Label>
                  <Select
                    required
                    value={editing.fileTypeId}
                    placeholder="Select file type"
                    showClearButton={false}
                    options={fileTypes.map((ft) => ({ value: ft.id, label: ft.name }))}
                    onChange={(e) => {
                      const fileTypeId = e.target.value ? Number(e.target.value) : '';
                      const ft = fileTypes.find((f) => f.id === fileTypeId);
                      const code = (ft?.code || 'INVOICE').toUpperCase();
                      setEditing({
                        ...editing,
                        fileTypeId,
                        lines: (PRESETS[code] || PRESETS.INVOICE).map((l) => ({ ...l })),
                        code: editing.code || `${code}_DEFAULT`,
                        name: editing.name || `${ft?.name || code} default`,
                      });
                    }}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className={cn('mb-1 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Scope</h3>
              <p className={cn('mb-3 text-sm', colors.textMuted)}>
                Supplier key binds the rule to one vendor (from document Creditor / Remember). Leave empty only for a
                file-type default that applies when the document has no creditor.
              </p>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Label>Supplier key</Label>
                  <TextInput
                    value={editing.supplierKey}
                    onChange={(e) => setEditing({ ...editing, supplierKey: e.target.value })}
                    placeholder="e.g. UNIBYTE ADVANCED IT — empty = any supplier (default only)"
                    showClearButton
                  />
                </div>
                <div>
                  <Label>Journal type</Label>
                  <Select
                    value={editing.documentTypeId ?? ''}
                    placeholder="None"
                    showClearButton
                    options={docTypes.map((d) => ({ value: d.id, label: d.name }))}
                    onChange={(e) =>
                      setEditing({ ...editing, documentTypeId: e.target.value ? Number(e.target.value) : '' })
                    }
                  />
                </div>
                <div>
                  <Label>Default company</Label>
                  <Select
                    value={editing.defaultCompanyId ?? ''}
                    placeholder="None"
                    showClearButton
                    options={companies.map((c) => ({ value: c.id, label: c.name }))}
                    onChange={(e) =>
                      setEditing({ ...editing, defaultCompanyId: e.target.value ? Number(e.target.value) : '' })
                    }
                  />
                </div>
              </div>
            </section>

            <section>
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                    Debit & credit roles
                  </h3>
                  <p className={cn('mt-1 text-sm', colors.textMuted)}>
                    Each row becomes journal line(s) when a document is scanned. Add extra EXPENSE rows with
                    keywords (e.g. software vs hardware) to route products to different accounts.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={applyPreset}>
                    Load preset
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setEditing({
                        ...editing,
                        lines: [...editing.lines, blankExpense(editing.lines.length)],
                      })
                    }
                    title="Add another EXPENSE account with product keywords"
                  >
                    + Product route
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() =>
                      setEditing({
                        ...editing,
                        lines: [
                          ...editing.lines,
                          {
                            sortOrder: editing.lines.length,
                            role: 'OTHER',
                            side: 'DEBIT',
                            accountId: '',
                            amountMode: 'TOTAL',
                            matchKeywords: '',
                          },
                        ],
                      })
                    }
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add role
                  </Button>
                </div>
              </div>

              <div className={cn('overflow-hidden rounded-xl border', colors.border)}>
                <div
                  className={cn(
                    'hidden grid-cols-[2rem_minmax(6rem,0.8fr)_minmax(6rem,0.7fr)_minmax(6rem,0.7fr)_minmax(10rem,1.2fr)_minmax(9rem,1fr)_2rem] gap-2 border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wider md:grid',
                    colors.border,
                    colors.textMuted,
                    colors.headerBg,
                  )}
                >
                  <span>#</span>
                  <span>Role</span>
                  <span>Side</span>
                  <span>Amount</span>
                  <span>Account</span>
                  <span>Keywords</span>
                  <span />
                </div>

                <div className={cn('divide-y', colors.divide)}>
                  {editing.lines.map((line, index) => (
                    <div
                      key={index}
                      className="grid gap-3 px-3 py-3 md:grid-cols-[2rem_minmax(6rem,0.8fr)_minmax(6rem,0.7fr)_minmax(6rem,0.7fr)_minmax(10rem,1.2fr)_minmax(9rem,1fr)_2rem] md:items-center md:gap-2"
                    >
                      <span className={cn('text-sm font-semibold tabular-nums', colors.textMuted)}>{index + 1}</span>

                      <div className="text-sm md:contents">
                        <span className={cn('mb-1 block font-medium md:hidden', colors.text)}>Role</span>
                        <Select
                          value={line.role}
                          showClearButton={false}
                          options={ROLES.map((r) => ({ value: r, label: r }))}
                          onChange={(e) => updateLine(index, { role: e.target.value as DocumentPostingRole })}
                        />
                      </div>

                      <div className="text-sm md:contents">
                        <span className={cn('mb-1 block font-medium md:hidden', colors.text)}>Side</span>
                        <div className="flex gap-1">
                          {SIDES.map((side) => (
                            <Button
                              key={side}
                              type="button"
                              size="sm"
                              variant={line.side === side ? 'default' : 'outline'}
                              onClick={() => updateLine(index, { side })}
                              className={cn(
                                'flex-1',
                                line.side === side &&
                                  (side === 'DEBIT'
                                    ? 'border-teal-600 bg-teal-600 text-white hover:bg-teal-700'
                                    : 'border-amber-600 bg-amber-600 text-white hover:bg-amber-700'),
                              )}
                            >
                              {side === 'DEBIT' ? 'Debit' : 'Credit'}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="text-sm md:contents">
                        <span className={cn('mb-1 block font-medium md:hidden', colors.text)}>Amount</span>
                        <Select
                          value={line.amountMode}
                          showClearButton={false}
                          title={amountModeLabel(line.amountMode)}
                          options={AMOUNT_MODES.map((m) => ({ value: m.value, label: m.short }))}
                          onChange={(e) =>
                            updateLine(index, { amountMode: e.target.value as DocumentPostingAmountMode })
                          }
                        />
                      </div>

                      <div className="text-sm md:contents">
                        <span className={cn('mb-1 block font-medium md:hidden', colors.text)}>Account</span>
                        <div className="flex min-w-0 items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className={cn(
                              'h-10 w-10 shrink-0',
                              colors.primaryFg,
                              colors.primaryBorderAll,
                              colors.interactiveHover,
                            )}
                            title="Browse chart of accounts"
                            aria-label="Browse chart of accounts"
                            onClick={() => setCoaPickerLineIndex(index)}
                          >
                            <ListTreeIcon className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <Select
                              required
                              value={line.accountId}
                              placeholder="Select account"
                              showClearButton={false}
                              title={(() => {
                                const selected = accounts.find((a) => a.id === line.accountId);
                                return selected ? accountLabel(selected) : undefined;
                              })()}
                              options={accounts.map((a) => ({ value: a.id, label: accountLabel(a) }))}
                              onChange={(e) =>
                                updateLine(index, {
                                  accountId: e.target.value ? Number(e.target.value) : '',
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="text-sm md:contents">
                        <span className={cn('mb-1 block font-medium md:hidden', colors.text)}>Keywords</span>
                        <TextInput
                          value={line.matchKeywords}
                          onChange={(e) => updateLine(index, { matchKeywords: e.target.value })}
                          placeholder={line.role === 'EXPENSE' ? 'e.g. software,office' : ''}
                          disabled={line.role !== 'EXPENSE'}
                          showClearButton={line.role === 'EXPENSE'}
                          title={
                            line.role === 'EXPENSE'
                              ? 'Comma-separated words matched against invoice / scan-line text. Leave empty for the default expense account.'
                              : 'Keywords apply to EXPENSE rows only'
                          }
                          className={cn(line.role !== 'EXPENSE' && 'opacity-50')}
                        />
                      </div>

                      <div className="flex justify-end">
                        {editing.lines.length > 1 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setEditing({
                                ...editing,
                                lines: editing.lines.filter((_, i) => i !== index),
                              })
                            }
                            className={cn(isDark ? 'text-red-400' : 'text-red-600', colors.interactiveHover)}
                            aria-label="Remove role"
                            title="Remove"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="h-9 w-9" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </form>

        <CoaAccountPickerModal
          isOpen={coaPickerLineIndex != null}
          onClose={() => setCoaPickerLineIndex(null)}
          value={
            coaPickerLineIndex != null ? editing.lines[coaPickerLineIndex]?.accountId || undefined : undefined
          }
          postingOnly
          onSelect={(accountId) => {
            if (coaPickerLineIndex == null) return;
            updateLine(coaPickerLineIndex, { accountId });
            setCoaPickerLineIndex(null);
          }}
        />
      </div>
    );
  }

  return (
    <GeneralList
      tabs={fileTypeTabs}
      activeTab={activeTab}
      onTabChange={(value) => setFilterFileTypeId(value === 'all' ? 'all' : Number(value))}
      actions={
        <Button type="button" onClick={() => openEditor()}>
          <PlusIcon className="h-4 w-4" />
          New rule
        </Button>
      }
      banner={
        error ? (
          <div className={cn('rounded-lg border px-3 py-2 text-sm', colors.border, colors.error)}>
            {error}
          </div>
        ) : null
      }
      isLoading={isLoading}
      loadingLabel="Loading rules…"
      isEmpty={filteredRules.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: rules.length === 0 ? 'No business rules yet' : 'No rules for this file type',
        hint:
          rules.length === 0
            ? 'Start with an invoice preset: expense debit from scan lines, payable credit to balance.'
            : 'Create a rule for this file type, or switch back to All.',
        action: (
          <Button type="button" onClick={() => openEditor()}>
            <PlusIcon className="h-4 w-4" />
            New rule
          </Button>
        ),
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className="w-[7.5rem] font-medium" />
            <th className="font-medium">Rule</th>
            <th className="hidden font-medium sm:table-cell">Scope</th>
            <th className="hidden font-medium md:table-cell">Posting</th>
            <th className="w-36 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredRules.map((rule) => (
            <tr
              key={rule.id}
              onClick={() => openEditor(rule)}
              className={cn(
                'group cursor-pointer border-b transition last:border-b-0',
                colors.border,
                colors.interactiveHover,
              )}
            >
              <td>
                <div className="flex items-center flex-nowrap">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditor(rule);
                    }}
                    aria-label="Edit rule"
                    title="Edit"
                    className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateRule(rule);
                    }}
                    aria-label="Duplicate rule"
                    title="Duplicate"
                    className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                  >
                    <CopyIcon className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const ok = await confirm({
                        title: 'Delete rule',
                        message: `Delete rule “${rule.name}”?`,
                        confirmText: 'Delete',
                        cancelText: 'Cancel',
                        confirmVariant: 'danger',
                      });
                      if (!ok) return;
                      await deleteDocumentPostingRule(rule.id);
                      await mutate();
                    }}
                    aria-label="Delete rule"
                    title="Delete"
                    className={cn(
                      'rounded-lg p-1 transition-colors',
                      isDark ? 'text-red-400' : 'text-red-600',
                      colors.interactiveHover,
                    )}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </td>
              <td className="max-w-0">
                <div className="min-w-0">
                  <p className={cn('truncate font-medium', colors.text)}>{rule.name}</p>
                  <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                    {rule.fileType?.name || `Type #${rule.fileTypeId}`} · {rule.code}
                  </p>
                  <p className={cn('mt-1 truncate text-xs sm:hidden', colors.textMuted)}>
                    {rule.supplierKey || 'Any supplier'}
                  </p>
                </div>
              </td>
              <td className={cn('hidden sm:table-cell', colors.textMuted)}>
                {rule.supplierKey ? (
                  <div className="truncate text-sm" title={rule.supplierKey}>
                    Supplier: {rule.supplierKey}
                  </div>
                ) : (
                  <div className="text-sm">Any supplier</div>
                )}
                {rule.documentType && (
                  <div className="mt-0.5 truncate text-xs opacity-80">{rule.documentType.code}</div>
                )}
              </td>
              <td className="hidden md:table-cell">
                <PostingChain
                  colors={colors}
                  isDark={isDark}
                  lines={rule.lines.map((line) => ({
                    role: line.role,
                    side: line.side,
                    account: line.account ? `${line.account.combLevel || line.account.code}` : null,
                  }))}
                />
              </td>
              <td className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                <Switch
                  size="sm"
                  label={rule.isActive === false ? 'Inactive' : 'Active'}
                  checked={rule.isActive !== false}
                  onChange={() => void toggleActive(rule)}
                  aria-label={rule.isActive === false ? 'Activate rule' : 'Deactivate rule'}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GeneralList>
  );
};
